import { CommonModule, DecimalPipe } from "@angular/common";
import { ModuleWithProviders, NgModule } from "@angular/core";
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { RouterModule } from "@angular/router";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatButtonModule } from "@angular/material/button";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { HeaderComponent } from "./components/header/header.component";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatSelectModule } from "@angular/material/select";
import { MatRadioModule } from "@angular/material/radio";
import { CommonShimmerComponent } from "./shimmers/common-shimmer/common-shimmer.component";
import { MatSliderModule } from "@angular/material/slider";
import { CarouselComponent } from "./components/carousel/carousel.component";
import { DefaultBlankPipe } from "./pipes/default-blank.pipe";
import { MatExpansionModule } from "@angular/material/expansion";
import { CustomDateFormatPipe } from "./pipes/custom-date.pipe";
import { ScrollTrackerDirective } from "./directive/scroll-tracker.directive";
import { GoogleMapsModule } from "@angular/google-maps";
import { MatIconModule } from "@angular/material/icon";
import { NotificationService } from "./components/notification/notification.service";
import { MatNativeDateModule, MatOptionModule } from "@angular/material/core";
import { LottieModule } from "ngx-lottie";
import { TableShimmerComponent } from "./shimmers/table-shimmer/table-shimmer.component";
import { NotificationShimmerComponent } from "./shimmers/notification-shimmer/notification-shimmer.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatMenuModule } from "@angular/material/menu";
import { ConfirmationCardComponent } from "./components/confirmation-card/confirmation-card.component";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { ProfileShimmerComponent } from "./shimmers/profile-shimmer/profile-shimmer.component";
import { SupportMessagePipe } from "./pipes/support-message.pipe";
import { SidebarComponent } from "./components/sidebar/sidebar.component";
import { PanelChargesDetailsComponent } from "./components/panel-charges-details/panel-charges-details.component";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { BrandLogoComponent } from "./components/brand-logo/brand-logo.component";
import { InfoErrorComponent } from "./components/info-error/info-error.component";
import { SpinnerComponent } from "./components/spinner/spinner.component";
import { AutoFocusDirective } from "./directive/auto-focus.directive";
import { InfoDialogComponent } from "./dialogs/info-dialog/info-dialog.component";
import { ConfirmationDialogComponent } from "./dialogs/confirmation-dialog/confirmation-dialog.component";
import { LoanSurveyComponent } from "./components/loan-survey/loan-survey.component";
import { CalendarComponent } from "./dialogs/calendar/calendar.component";
import { OrdinalPipe } from "./directive/ordinal.pipe";
import { OrderByPipe } from "./pipes/order-by.pipe";
import { HistoryShimmerComponent } from "./shimmers/history-shimmer/history-shimmer.component";
import { PointsShimmerComponent } from "./shimmers/points-shimmer/points-shimmer.component";
import { AlertCardsComponent } from "./components/alert-cards/alert-cards.component";
import { TermsAndConditionsComponent } from "./dialogs/terms-and-conditions/terms-and-conditions.component";
import { CelebrationComponent } from "./components/celebration/celebration.component";
import { TimeLineShimmerComponent } from "./shimmers/time-line-shimmer/time-line-shimmer.component";
import { UserApprovalShimmerComponent } from "./shimmers/user-approval-shimmer/user-approval-shimmer.component";
import { PennyDropShimmerComponent } from "./shimmers/penny-drop-shimmer/penny-drop-shimmer.component";
import { LoanSurveyShimmerComponent } from "./shimmers/loan-survey-shimmer/loan-survey-shimmer.component";
import { SliderComponent } from "./components/slider/slider.component";
import { NoDataComponent } from "./components/no-data/no-data.component";
import { KeyboardNavigationDirective } from "./directive/list-keyboard-navigation.directive.ts";
import { FooterVisibleScrollDirective } from "./directive/footer-visible-scroll.directive";
import { PrimeCardLayoutDirective } from "./directive/prime-card-layout.directive";
import { TrimDirective } from "./directive/trim.directive";
import { SvgIconDirective } from "./directive/svg-icon.directive";
import { OtpDigitsOnlyDirective } from "./directive/otp-digits-only.directive";
import { TextFieldModule } from "@angular/cdk/text-field";
import { TabGroupComponent } from "./components/tab-group/tab-group.component";

export function playerFactory(): any {
    return import("lottie-web");
}

const Modules = [
    CommonModule,
    RouterModule,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    TextFieldModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSliderModule,
    MatExpansionModule,
    MatIconModule,
    GoogleMapsModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatMenuModule,
    MatNativeDateModule,
    MatDatepickerModule,
    MatSlideToggleModule,
];

const Components = [
    HeaderComponent,
    InfoDialogComponent,
    ConfirmationDialogComponent,
    InfoDialogComponent,
    CommonShimmerComponent,
    PointsShimmerComponent,
    HistoryShimmerComponent,
    TimeLineShimmerComponent,
    TableShimmerComponent,
    NotificationShimmerComponent,
    ProfileShimmerComponent,
    LoanSurveyShimmerComponent,
    PennyDropShimmerComponent,
    CarouselComponent,
    ConfirmationCardComponent,
    SidebarComponent,
    PanelChargesDetailsComponent,
    UserApprovalShimmerComponent,
    BrandLogoComponent,
    InfoErrorComponent,
    SpinnerComponent,
    LoanSurveyComponent,
    CalendarComponent,
    AlertCardsComponent,
    TermsAndConditionsComponent,
    CelebrationComponent,
    SliderComponent,
    NoDataComponent,
    TabGroupComponent,
];

const Pipes = [DefaultBlankPipe, CustomDateFormatPipe, SupportMessagePipe, OrderByPipe];

const Directives = [
    ScrollTrackerDirective,
    FooterVisibleScrollDirective,
    OrdinalPipe,
    AutoFocusDirective,
    KeyboardNavigationDirective,
    PrimeCardLayoutDirective,
    TrimDirective,
    SvgIconDirective,
    OtpDigitsOnlyDirective,
];

@NgModule({
    imports: [...Modules, LottieModule.forRoot({ player: playerFactory })],

    declarations: [...Components, ...Pipes, ...Directives],

    exports: [...Modules, ...Components, ...Pipes, ...Directives],

    providers: [
        { provide: MAT_DIALOG_DATA, useValue: {} },
        { provide: MatDialogRef, useValue: {} },
        DecimalPipe,
        ...Pipes,
        NotificationService,
    ],
})
export class SharedModule {
    static forRoot(): ModuleWithProviders<SharedModule> {
        return {
            ngModule: SharedModule,
            providers: [],
        };
    }
}
